import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { OrganisationVisibility } from 'api/organisations/organisationApitypes';
import AddUserOptions from 'pages/AddUser/AddUserForm/AddUserOptions/AddUserOptions';
import ExistingUserDetails from 'pages/AddUser/AddUserForm/ExistingUserDetails/ExistingUserDetails';
import { getUserOptions } from 'pages/AddUser/AddUserForm/helper';
import { useInviteContext } from 'pages/AddUser/InviteProvider';
import { useTranslation } from 'react-i18next';
import { isGroupManager, isSuperAdmin } from 'utils/role';
import { ExistingUserSectionProps } from './proptypes';

export const ExistingUserSection = ({
  user,
  selectedOrgs,
  onRemoveOrg,
  groupOptions,
  setSelectedOrgs,
}: ExistingUserSectionProps) => {
  const { t } = useTranslation();
  const {
    practitionerOrganisations,
    rawPractitionerOrganisations,
    practitionerOrgsInGroups,
    practitionerGroupCount,
    practitionerOrgCount,
    groups,
    organisationsInGroups,
    setFormState,
    formState: { selectedGroup, orgType, existingPractitioner },
  } = useInviteContext();
  const getOrganisations = () => {
    if (existingPractitioner?.role === 'SUPER_ADMIN') {
      return practitionerOrganisations.filter(
        ({
          id,
          visibility,
        }: {
          id: string;
          visibility?: OrganisationVisibility;
        }) => id === 'insights' || visibility === 'PRIVATE',
      );
    }

    if (
      existingPractitioner?.role &&
      ['GROUP_MANAGER', 'ADVANCED_USER'].includes(existingPractitioner.role)
    ) {
      return practitionerOrganisations ?? [];
    }
    return rawPractitionerOrganisations;
  };

  const onlyInviteToSpecialHandling =
    isSuperAdmin(user?.role) && isSuperAdmin(existingPractitioner?.role);

  return (
    <>
      <ExistingUserDetails
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        user={existingPractitioner!}
        organisations={getOrganisations()}
        orgsInGroups={
          existingPractitioner?.role === 'ADVANCED_USER'
            ? practitionerOrgsInGroups
            : {}
        }
        groupCount={
          existingPractitioner?.role === 'ADVANCED_USER'
            ? practitionerGroupCount
            : 0
        }
        organisationCount={practitionerOrgCount}
      />
      <AddUserOptions
        selectedOrgs={selectedOrgs}
        setSelectedOrgs={setSelectedOrgs}
        setSelectedGroup={(group) => {
          setFormState({ selectedGroup: group });
        }}
        selectedGroup={selectedGroup}
        orgType={orgType}
        setOrgType={(type) => {
          setFormState({ orgType: type });
        }}
        onlySpecialHandling={onlyInviteToSpecialHandling}
        groups={
          onlyInviteToSpecialHandling
            ? groups.filter(({ organisations: groupOrgs }) =>
                groupOrgs.some(({ visibility }) => visibility === 'PRIVATE'),
              )
            : groups
        }
        organisationsInGroups={organisationsInGroups}
        onRemoveOrg={onRemoveOrg}
        existingUser
        titleKey="ui.event-management.users.add-existing-practitioner.new-organisations.title"
        options={getUserOptions(
          t,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          existingPractitioner!,
          isGroupManager(user?.role),
        )}
        groupOptions={groupOptions}
      />
      {selectedOrgs.length ? (
        <Grid
          container
          sx={{
            justifyContent: 'flex-end',
            mt: '2.5rem',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            sx={{
              width: '252px',
              padding: '10px 20px',
            }}
          >
            {t(
              'ui.event-management.users.add-existing-practitioner.new-organisations.add-user',
            )}
          </Button>
        </Grid>
      ) : null}
    </>
  );
};
